import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, RotateCcw, Shield, Eye, FileText, AlertTriangle } from 'lucide-react';
import { useConfirm } from '../../components/common/ConfirmDialog';

const actionColors = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  view: 'bg-gray-100 text-gray-700',
  revert: 'bg-purple-100 text-purple-700',
  sign: 'bg-yellow-100 text-yellow-700',
  send: 'bg-cyan-100 text-cyan-700',
  open: 'bg-orange-100 text-orange-700',
};

const AuditLogViewer = ({ tenantId = null, isSystemOwner = false }) => {
  const [logs, setLogs] = useState([]);
  const confirm = useConfirm();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState(tenantId || '');
  const [tenants, setTenants] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50 });
      const tid = tenantFilter || tenantId;
      if (tid) params.append('tenant_id', tid);
      if (search) params.append('search', search);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entity_type', entityFilter);
      const res = await api.get(`/audit/logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/audit/stats');
      setStats(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    if (isSystemOwner) {
      api.get('/tenants').then(res => setTenants(res.data || [])).catch(() => {});
    }
  }, []);

  const handleRevert = async (logId) => {
    if (!await confirm({ title: 'Are you sure you want to revert this change? This will restore the previous values.', destructive: true })) return;
    try {
      const res = await api.post(`/audit/revert/${logId}`);
      toast.success(`Reverted: ${res.data.reverted_fields?.join(', ')}`);
      fetchLogs(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert');
    }
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Events</p>
          </CardContent></Card>
          {['create', 'update', 'delete', 'view'].map(a => (
            <Card key={a}><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.by_action?.[a] || 0}</p>
              <p className="text-xs text-muted-foreground capitalize">{a}s</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Audit Trail
          </CardTitle>
          <div className="flex flex-wrap gap-3 mt-2">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by name, email, entity..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') fetchLogs(1); }} className="pl-9 h-8 text-sm" />
            </div>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[140px] h-8 text-sm"><SelectValue placeholder="All Actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="revert">Revert</SelectItem>
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[160px] h-8 text-sm"><SelectValue placeholder="All Entities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="crm_entity">CRM Entity</SelectItem>
                <SelectItem value="horse">Horse</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="sale_opportunity">Sale Opportunity</SelectItem>
                <SelectItem value="file">File</SelectItem>
                <SelectItem value="owner_portal">Owner Portal</SelectItem>
              </SelectContent>
            </Select>
            {isSystemOwner && tenants.length > 0 && (
              <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[180px] h-8 text-sm"><SelectValue placeholder="All Tenants" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" className="h-8" onClick={() => fetchLogs(1)}>Apply</Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 py-0 pb-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp (UTC)</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {loading ? 'Loading...' : 'No audit logs found'}
                  </TableCell>
                </TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedLog(log)}>
                  <TableCell className="text-xs font-mono">{log.timestamp ? new Date(log.timestamp).toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</TableCell>
                  <TableCell className="text-xs">
                    <div>
                      <p className="font-medium">{log.user_email}</p>
                      <p className="text-muted-foreground">{log.user_role}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge className={`text-xs pointer-events-none capitalize ${actionColors[log.action] || 'bg-gray-100 text-gray-700'}`}>{log.action}</Badge></TableCell>
                  <TableCell className="text-xs capitalize">{log.entity_type?.replace(/_/g, ' ')}</TableCell>
                  <TableCell className="text-xs font-medium">{log.entity_name || log.entity_id?.slice(0, 8)}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{log.ip_address || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><Eye className="w-3 h-3" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <p className="text-xs text-muted-foreground">{total} total events • Page {page} of {pages}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7" disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="h-7" disabled={page >= pages} onClick={() => fetchLogs(page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(v) => { if (!v) setSelectedLog(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge className={`capitalize ${actionColors[selectedLog.action] || ''}`}>{selectedLog.action}</Badge>
                  {selectedLog.entity_type?.replace(/_/g, ' ')} — {selectedLog.entity_name || selectedLog.entity_id}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-muted-foreground">User</p><p className="font-medium">{selectedLog.user_email}</p></div>
                  <div><p className="text-xs text-muted-foreground">Role</p><p>{selectedLog.user_role}</p></div>
                  <div><p className="text-xs text-muted-foreground">Timestamp (UTC)</p><p className="font-mono text-xs">{selectedLog.timestamp}</p></div>
                  <div><p className="text-xs text-muted-foreground">IP Address</p><p className="font-mono text-xs">{selectedLog.ip_address || '-'}</p></div>
                  <div className="col-span-2"><p className="text-xs text-muted-foreground">User Agent</p><p className="font-mono text-[10px] break-all">{selectedLog.user_agent || '-'}</p></div>
                </div>

                {/* Field Changes */}
                {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-2">Field Changes</p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field</TableHead>
                          <TableHead>Before</TableHead>
                          <TableHead>After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(selectedLog.changes).map(([field, vals]) => (
                          <TableRow key={field}>
                            <TableCell className="text-xs font-medium">{field.replace(/_/g, ' ')}</TableCell>
                            <TableCell className="text-xs text-red-600 font-mono">{JSON.stringify(vals.old)}</TableCell>
                            <TableCell className="text-xs text-green-600 font-mono">{JSON.stringify(vals.new)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Metadata</p>
                    <pre className="text-[10px] bg-muted p-2 rounded overflow-auto">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  </div>
                )}
              </div>
              <DialogFooter>
                {selectedLog.action === 'update' && selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                  <Button variant="outline" onClick={() => handleRevert(selectedLog.id)} className="text-orange-600">
                    <RotateCcw className="w-4 h-4 mr-1" /> Revert This Change
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelectedLog(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogViewer;
